import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/20/solid';
import { computeLoanTermYears, calcGuaranteeFee, calcAnnualPmt, calcDSCR } from '../../utils/loanMath';

const PrequalFinancialMetricsDisplay = ({ ocrData = {}, loanData = {} }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const years = Object.keys(ocrData).sort((a, b) => {
    return new Date(a) - new Date(b);
  });

  const calculateEBITDAOwnersComp = (yearData) => {
    const baseIncome = Number(yearData.ordinary_business_income || 0);
    const originalOfficerCompensation = Number(yearData.compensation_of_officers || 0);
    const interest = Number(yearData.interest || 0);
    const depreciation = Number(yearData.depreciation || 0);
    const amortization = Number(yearData.amortization || 0);
    
    return baseIncome + originalOfficerCompensation + interest + depreciation + amortization;
  };

  const calculateAddBacks = (otherDeductions) => {
    // If otherDeductions is an array, sum up all the amounts
    if (Array.isArray(otherDeductions)) {
      return otherDeductions.reduce((total, deduction) => {
        return total + Number(deduction.amount || 0);
      }, 0);
    }
    
    // For backward compatibility, if otherDeductions is an object
    if (typeof otherDeductions === 'object' && otherDeductions !== null) {
      return Object.entries(otherDeductions).reduce((total, [category, amount]) => {
        return total + Number(amount || 0);
      }, 0);
    }
    
    return 0;
  };

  const calculateSDE = (yearData) => {
    if (!yearData) return 0;
    const ebitdaOwnersComp = calculateEBITDAOwnersComp(yearData);
    const addbacksTotal = calculateAddBacks(yearData.other_deductions || []);
    
    return ebitdaOwnersComp + addbacksTotal;
  };

  const calculateNOI = (yearData) => {
    if (!yearData) return 0;
    const sde = calculateSDE(yearData);
    const newOfficerCompensation = Number(yearData.new_officer_compensation || 75000);
    return sde - newOfficerCompensation;
  };

  const getAnnualDebtService = () => {
    const purchasePrice = Number(loanData.prequalifiedAmount || loanData.purchasePrice || 0);
    const realEstateValue = Number(loanData.realEstateValue || 0); // Assumes loanData contains realEstateValue
    const sba7aPercentage = 0.90; // This seems to be a specific assumption for this component
    const totalFinancedAmount = purchasePrice * sba7aPercentage;
    
    if (totalFinancedAmount <= 0) {
      return 0;
    }

    // Calculate interest rate based on prequalification type
    const baseRate = 7.5; // Current prime rate
    const interestRateAddon = loanData.prequalificationType === 'Easy Financing' ? 2.0 : 2.5;
    const interestRateDecimal = (baseRate + interestRateAddon) / 100; // CORRECT: For utility
    const displayInterestRate = baseRate + interestRateAddon; // CORRECT: For display string
    
    const termYears = computeLoanTermYears({ purchasePrice, realEstateValue });

    return calcAnnualPmt({ 
      principal: totalFinancedAmount, 
      rate: interestRateDecimal, // Use decimal for calculation
      termYears 
    });
  };

  const getDSCR = (noi) => {
    const annualDebtService = getAnnualDebtService();
    return calcDSCR({ noi, annualDebtService });
  };

  const getFinancialData = (key) => {
    return years.map(year => {
      const yearData = ocrData[year] || {};
      let value;
      switch (key) {
        case 'dscr':
          value = getDSCR(calculateNOI(yearData));
          break;
        case 'noi':
          value = calculateNOI(yearData);
          break;
        case 'ordinaryBusinessIncome':
          value = yearData.ordinary_business_income || 0;
          break;
        default:
          value = 0;
      }
      return { name: year.replace('Business Tax Return - ', ''), value };
    });
  };

  const financialStats = [
    { 
      name: 'DSCR', 
      stat: formatNumber(getDSCR(calculateNOI(ocrData[years[years.length-1]] || {}))),
      previousStat: formatNumber(getDSCR(calculateNOI(ocrData[years[years.length-2]] || {}))),
      change: ((getDSCR(calculateNOI(ocrData[years[years.length-1]] || {})) / getDSCR(calculateNOI(ocrData[years[years.length-2]] || {})) - 1) * 100).toFixed(2) + '%',
      changeType: getDSCR(calculateNOI(ocrData[years[years.length-1]] || {})) > getDSCR(calculateNOI(ocrData[years[years.length-2]] || {})) ? 'increase' : 'decrease',
      data: getFinancialData('dscr'),
      color: '#008081'
    },
    { 
      name: 'Net Operating Income', 
      stat: formatCurrency(calculateNOI(ocrData[years[years.length-1]] || {})),
      previousStat: formatCurrency(calculateNOI(ocrData[years[years.length-2]] || {})),
      change: ((calculateNOI(ocrData[years[years.length-1]] || {}) / calculateNOI(ocrData[years[years.length-2]] || {}) - 1) * 100).toFixed(2) + '%',
      changeType: calculateNOI(ocrData[years[years.length-1]] || {}) > calculateNOI(ocrData[years[years.length-2]] || {}) ? 'increase' : 'decrease',
      data: getFinancialData('noi'),
      color: '#6593F5'
    },
    { 
      name: 'Ordinary Business Income', 
      stat: formatCurrency(Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0)),
      previousStat: formatCurrency(Number(ocrData[years[years.length-2]]?.ordinary_business_income || 0)),
      change: ((Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0) / Number(ocrData[years[years.length-2]]?.ordinary_business_income || 1) - 1) * 100).toFixed(2) + '%',
      changeType: Number(ocrData[years[years.length-1]]?.ordinary_business_income || 0) > Number(ocrData[years[years.length-2]]?.ordinary_business_income || 0) ? 'increase' : 'decrease',
      data: getFinancialData('ordinaryBusinessIncome'),
      color: '#3FE0D0'
    }
  ];

  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {financialStats.map((item) => (
          <FinancialStatCard key={item.name} item={item} />
        ))}
      </div>

      <table className="min-w-full bg-white border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2 border-b text-left">Metric</th>
            {years.map(year => (
              <th key={year} className="px-4 py-2 border-b text-right">{year.replace('Business Tax Return - ', '')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            { key: 'gross_receipts_or_sales', label: 'Gross Receipts or Sales' },
            { key: 'cost_of_goods_sold', label: 'Cost of Goods Sold', isNegative: true },
            { key: 'ordinary_business_income', label: 'Ordinary Business Income', isBold: true },
            { key: 'compensation_of_officers', label: '+ Original Officer Compensation' },
            { key: 'interest', label: '+ Interest' },
            { key: 'depreciation', label: '+ Depreciation' },
            { key: 'amortization', label: '+ Amortization' },
            { key: 'ebitdaOwnersComp', label: 'EBITDA + Owners Comp', calculate: calculateEBITDAOwnersComp, isBold: true, hasBorder: true },
            { key: 'ebitdaOwnersCompPercentage', label: 'EBITDA + Owners Comp %', calculate: (yearData) => (calculateEBITDAOwnersComp(yearData) / Number(yearData.gross_receipts_or_sales || 1)) * 100, isPercentage: true },
          ].map(({ key, label, calculate, isBold, hasBorder, isNegative, isPercentage }) => (
            <tr key={key} className={`hover:bg-gray-50 ${hasBorder ? 'border-t-2 border-gray-300' : ''}`}>
              <td className={`px-4 py-2 border-b ${isBold ? 'font-bold' : 'font-medium'} ${isNegative ? 'text-red-600' : ''}`}>{label}</td>
              {years.map(year => (
                <td key={year} className={`px-4 py-2 border-b text-right ${isBold ? 'font-bold' : ''} ${isNegative ? 'text-red-600' : ''}`}>
                  {calculate 
                    ? (isPercentage
                        ? `${formatNumber(calculate(ocrData[year]))}%`
                        : formatCurrency(calculate(ocrData[year])))
                    : (isPercentage
                        ? `${formatNumber((Number(ocrData[year]?.[key]) / Number(ocrData[year]?.gross_receipts_or_sales || 1)) * 100)}%`
                        : formatCurrency(Number(ocrData[year]?.[key] || 0)))}
                </td>
              ))}
            </tr>
          ))}
          <tr className="bg-gray-100">
            <td colSpan={years.length + 1} className="px-4 py-2 border-b font-bold">Add Backs</td>
          </tr>
          {years.map(year => {
            const yearData = ocrData[year] || {};
            const addBacks = Array.isArray(yearData.other_deductions) ? yearData.other_deductions : [];
            
            return addBacks.length > 0 ? (
              // If we have add-backs, display each one
              addBacks.map((addBack, index) => (
                <tr key={`${year}-${index}`} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border-b pl-8 font-medium text-green-600">+ {addBack.category}</td>
                  {years.map(y => (
                    <td key={y} className="px-4 py-2 border-b text-right text-green-600">
                      {y === year ? formatCurrency(Number(addBack.amount || 0)) : ''}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // If no add-backs, display a single empty row
              <tr key={`${year}-empty`} className="hover:bg-gray-50">
                <td className="px-4 py-2 border-b pl-8 font-medium text-gray-400">No add-backs</td>
                {years.map(y => (
                  <td key={y} className="px-4 py-2 border-b text-right"></td>
                ))}
              </tr>
            );
          })}
          {[
            { key: 'sde', label: 'Seller\'s Discretionary Earnings (SDE)', calculate: calculateSDE, isBold: true, hasBorder: true },
            { key: 'newOfficerCompensation', label: '- New Officer Compensation', calculate: (yearData) => -(Number(yearData.new_officer_compensation || 75000)), isNegative: true },
            { key: 'noi', label: 'Net Operating Income (NOI)', calculate: calculateNOI, isBold: true, hasBorder: true },
            { key: 'annualDebtService', label: 'Annual Debt Service', calculate: () => getAnnualDebtService(), isBold: false },
          ].map(({ key, label, calculate, isBold, hasBorder, isNegative, isRatio }) => (
            <tr key={key} className={`hover:bg-gray-50 ${hasBorder ? 'border-t-2 border-gray-300' : ''}`}>
              <td className={`px-4 py-2 border-b ${isBold ? 'font-bold' : 'font-medium'} ${isNegative ? 'text-red-600' : ''}`}>{label}</td>
              {years.map(year => (
                <td key={year} className={`px-4 py-2 border-b text-right ${isBold ? 'font-bold' : ''} ${isNegative ? 'text-red-600' : ''}`}>
                  {isRatio
                    ? formatNumber(calculate(ocrData[year]))
                    : formatCurrency(calculate(ocrData[year]))}
                </td>
              ))}
            </tr>
          ))}
          
          {/* Debt Service Calculation Details */}
          {(() => {
            const purchasePrice = Number(loanData.prequalifiedAmount || loanData.purchasePrice || 0);
            const realEstateValue = Number(loanData.realEstateValue || 0); // Assumes loanData contains realEstateValue
            const sba7aPercentage = 0.90;
            
            // Total financed amount is 90% of prequalified amount (10% is equity injection)
            const totalFinancedAmount = purchasePrice * sba7aPercentage;
            
            // Calculate fees
            const packagingFee = 2500;
            const closingCosts = 10000;
            const sbaGuaranteeFee = calcGuaranteeFee(totalFinancedAmount);
            
            // Business acquisition amount is what's left after fees
            const businessAcquisitionAmount = totalFinancedAmount - sbaGuaranteeFee - packagingFee - closingCosts;
            
            // Calculate interest rate based on prequalification type
            const baseRate = 7.5; // Current prime rate
            const interestRateAddon = loanData.prequalificationType === 'Easy Financing' ? 2.0 : 2.5;
            const displayInterestRate = baseRate + interestRateAddon; // Use a consistent variable name for display
            let termYearsForDisplay = computeLoanTermYears({ purchasePrice, realEstateValue }); // Calculate term for display
            if (isNaN(termYearsForDisplay)) {
              termYearsForDisplay = 10; // Default if calculation resulted in NaN (e.g. bad inputs further up)
            }
            
            return [
              { 
                key: 'totalFinanced', 
                label: '   Total Financed Amount (90%)', 
                value: totalFinancedAmount,
                isBold: true
              },
              { 
                key: 'guaranteeFee', 
                label: '      SBA Guarantee Fee', 
                value: sbaGuaranteeFee 
              },
              { 
                key: 'packagingFee', 
                label: '      Packaging Fee', 
                value: packagingFee 
              },
              { 
                key: 'closingCosts', 
                label: '      Closing Costs', 
                value: closingCosts 
              },
              { 
                key: 'businessAcquisition', 
                label: '      Business Acquisition Amount', 
                value: businessAcquisitionAmount,
              },
              {
                key: 'interestRate',
                label: '   Interest Rate',
                value: `${displayInterestRate.toFixed(2)}%`, // CORRECT: Use displayInterestRate for the string
                isPercentage: true
              },
              {
                key: 'loanTermDisplay',
                label: '   Loan Term',
                value: termYearsForDisplay, // Store the number
                isPercentage: false,
                isSpecialFormat: true // Add a flag for custom formatting
              }
            ].map(({ key, label, value, isBold, isPercentage, isSpecialFormat }) => (
              <tr key={key} className="hover:bg-gray-50 text-gray-600">
                <td className={`px-4 py-2 border-b pl-8 ${isBold ? 'font-medium' : ''}`}>{label}</td>
                {years.map((year, idx) => (
                  <td key={`${year}-${idx}`} className={`px-4 py-2 border-b text-right ${isBold ? 'font-medium' : ''}`}>
                    {idx === 0 ? 
                      (isSpecialFormat ? 
                        (key === 'loanTermDisplay' ? `${value} Years` : value) // Handle loan term display
                        : (isPercentage ? value : formatCurrency(value)))
                      : ''}
                  </td>
                ))}
              </tr>
            ));
          })()}
          
          {/* DSCR line */}
          <tr className="hover:bg-gray-50 border-t- border-gray-300">
            <td className="px-4 py-2 border-b font-bold">Debt Service Coverage Ratio (DSCR)</td>
            {years.map(year => (
              <td key={year} className="px-4 py-2 border-b text-right font-bold">
                {formatNumber(getDSCR(calculateNOI(ocrData[year])))}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const FinancialStatCard = ({ item }) => (
  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
    <dt className="truncate text-sm font-medium text-gray-500">{item.name}</dt>
    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{item.stat}</dd>
    <dd className="mt-2 flex items-baseline justify-between md:block lg:flex">
      <div className="flex items-baseline text-sm font-semibold text-green-600">
        {item.changeType === 'increase' ? (
          <ArrowUpIcon className="h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
        ) : (
          <ArrowDownIcon className="h-5 w-5 flex-shrink-0 self-center text-red-500" aria-hidden="true" />
        )}
        <span className="sr-only"> {item.changeType === 'increase' ? 'Increased' : 'Decreased'} by </span>
        {item.change}
      </div>
      <div className="inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0">
        from {item.previousStat}
      </div>
    </dd>
    <div className="mt-3">
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={item.data}>
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value" fill={item.color} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default PrequalFinancialMetricsDisplay;